/**
 * Walkthrough.js
 *
 * Guided tour. The overlay locks the page - user clicks Next to advance.
 * Each step has a selector to spotlight and optional onEnter action that
 * runs when entering the step (after user clicks Next on the previous one).
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import { Icon, Button } from '@material-ui/core';
import { buildWalkthroughSteps } from './walkthroughSteps';
import * as Actions from './store/actions/walkthrough.actions';

var useStyles = makeStyles(function () {
    return {
        overlay: {
            position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
            zIndex: 99998, pointerEvents: 'auto'
        },
        spotlight: {
            position: 'fixed', zIndex: 99999, borderRadius: 8,
            transition: 'top 0.4s ease, left 0.4s ease, width 0.4s ease, height 0.4s ease, opacity 0.8s ease',
            boxShadow: '0 0 0 100vmax rgba(0, 0, 0, 0.7)', pointerEvents: 'none'
        },
        pulseRing: {
            position: 'fixed', zIndex: 99999, borderRadius: 8,
            border: '2px solid rgba(59, 130, 246, 0.6)',
            transition: 'top 0.4s ease, left 0.4s ease, width 0.4s ease, height 0.4s ease',
            pointerEvents: 'none', animation: '$pulse 2s ease-in-out infinite'
        },
        '@keyframes pulse': {
            '0%': { transform: 'scale(1)', opacity: 1 },
            '50%': { transform: 'scale(1.06)', opacity: 0.7 },
            '100%': { transform: 'scale(1)', opacity: 1 }
        },
        tooltip: {
            position: 'fixed', zIndex: 100001, maxWidth: 420,
            maxHeight: 'calc(100vh - 32px)', overflowY: 'auto',
            padding: '16px 20px', backgroundColor: '#1e293b', color: '#f1f5f9',
            borderRadius: 12, fontSize: 14, lineHeight: 1.6, fontWeight: 500,
            boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
            transition: 'top 0.4s ease, left 0.4s ease',
            pointerEvents: 'auto'
        },
        tooltipArrow: {
            position: 'absolute', width: 12, height: 12,
            backgroundColor: '#1e293b', transform: 'rotate(45deg)'
        },
        stepCounter: {
            fontSize: 11, color: '#94a3b8', marginBottom: 6,
            fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase'
        },
        buttonRow: {
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14
        },
        nextButton: {
            backgroundColor: '#3b82f6', color: '#fff', fontSize: 13, fontWeight: 600,
            padding: '6px 18px', borderRadius: 8, textTransform: 'none',
            '&:hover': { backgroundColor: '#2563eb' }
        },
        skipButton: {
            color: '#94a3b8', fontSize: 12, fontWeight: 500, textTransform: 'none',
            padding: '4px 8px',
            '&:hover': { color: '#cbd5e1', backgroundColor: 'rgba(255,255,255,0.05)' }
        },
        cursor: {
            position: 'fixed', zIndex: 100000, pointerEvents: 'none',
            transition: 'top 0.5s ease, left 0.5s ease',
            filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.4))'
        }
    };
});

function findTarget(selector) {
    if (!selector) return null;
    return document.querySelector(selector);
}

function getRect(el, padding) {
    var p = padding || 8;
    var r = el.getBoundingClientRect();
    return { top: r.top - p, left: r.left - p, width: r.width + p * 2, height: r.height + p * 2 };
}

function tooltipPosition(spot, tw, th, preferredSide) {
    if (!spot) return null;
    var vw = window.innerWidth, vh = window.innerHeight, gap = 12;
    var margin = 8;
    var pos = { top: 0, left: 0, arrowSide: 'top', arrowLeft: 20 };

    // Helpers that produce a fully-positioned tooltip on a given side (or null if it doesn't fit)
    function placeRight() {
        if (spot.left + spot.width + gap + tw >= vw) return null;
        var p = { top: Math.max(margin, Math.min(spot.top, vh - th - margin)),
                  left: spot.left + spot.width + gap,
                  arrowSide: 'left', arrowLeft: 20 };
        p.arrowLeft = Math.max(16, Math.min(spot.top + spot.height / 2 - p.top, th - 16));
        return p;
    }
    function placeLeft() {
        if (spot.left - gap - tw <= margin) return null;
        var p = { top: Math.max(margin, Math.min(spot.top, vh - th - margin)),
                  left: spot.left - gap - tw,
                  arrowSide: 'right', arrowLeft: 20 };
        p.arrowLeft = Math.max(16, Math.min(spot.top + spot.height / 2 - p.top, th - 16));
        return p;
    }
    function placeBottom() {
        if (spot.top + spot.height + gap + th >= vh) return null;
        var p = { top: spot.top + spot.height + gap, left: 0, arrowSide: 'top', arrowLeft: 20 };
        p.left = Math.max(margin, Math.min(spot.left, vw - tw - margin));
        p.top = Math.max(margin, Math.min(p.top, vh - th - margin));
        p.arrowLeft = Math.max(16, Math.min(spot.left + spot.width / 2 - p.left, tw - 16));
        return p;
    }
    function placeTop() {
        if (spot.top - gap - th <= margin) return null;
        var p = { top: spot.top - gap - th, left: 0, arrowSide: 'bottom', arrowLeft: 20 };
        p.left = Math.max(margin, Math.min(spot.left, vw - tw - margin));
        p.top = Math.max(margin, Math.min(p.top, vh - th - margin));
        p.arrowLeft = Math.max(16, Math.min(spot.left + spot.width / 2 - p.left, tw - 16));
        return p;
    }

    // If the step declares a preferred side, honour it (pick side with more room when "side")
    if (preferredSide === 'right') {
        var r = placeRight(); if (r) return r;
        var l = placeLeft(); if (l) return l;
    } else if (preferredSide === 'left') {
        var l2 = placeLeft(); if (l2) return l2;
        var r2 = placeRight(); if (r2) return r2;
    } else if (preferredSide === 'side') {
        // Whichever horizontal side has more room
        var rightRoom = vw - (spot.left + spot.width);
        var leftRoom = spot.left;
        if (rightRoom >= leftRoom) {
            var rs = placeRight(); if (rs) return rs;
            var ls = placeLeft(); if (ls) return ls;
        } else {
            var ls2 = placeLeft(); if (ls2) return ls2;
            var rs2 = placeRight(); if (rs2) return rs2;
        }
    }

    // Default order: bottom, top, right, left
    return placeBottom() || placeTop() || placeRight() || placeLeft() || (function () {
        // Fallback: center in viewport
        pos.top = Math.max(margin, (vh - th) / 2);
        pos.left = Math.max(margin, Math.min(spot.left, vw - tw - margin));
        pos.arrowSide = 'none';
        pos.top = Math.max(margin, Math.min(pos.top, vh - th - margin));
        pos.arrowLeft = Math.max(16, Math.min(spot.left + spot.width / 2 - pos.left, tw - 16));
        return pos;
    })();
}

var Walkthrough = function () {
    var classes = useStyles();
    var dispatch = useDispatch();
    var history = useHistory();

    var active = useSelector(function (s) { return s.walkthroughState.walkthrough.active; });
    var currentStep = useSelector(function (s) { return s.walkthroughState.walkthrough.currentStep; });
    var completed = useSelector(function (s) { return s.walkthroughState.walkthrough.completed; });
    var enabled = useSelector(function (s) { return s.walkthroughState.walkthrough.enabled; });
    var role = useSelector(function (s) { return s.auth.user.role || ''; });
    var user = useSelector(function (s) { return s.auth.user; });

    var [steps, setSteps] = useState([]);
    var [spotRect, setSpotRect] = useState(null);
    var [tipPos, setTipPos] = useState(null);
    var [cursorPos, setCursorPos] = useState(null);
    var [ready, setReady] = useState(false);
    var [fadeIn, setFadeIn] = useState(false); // smooth fade-in on first show

    var tooltipRef = useRef(null);
    var timerRef = useRef(null);
    var tipSize = useRef({ w: 320, h: 140 });

    // Build steps
    useEffect(function () {
        setSteps(buildWalkthroughSteps({ role: role }));
    }, [role]);

    // Auto-start (only for instructors/admins, not participants)
    var isParticipant = role && role.indexOf('participant') !== -1;
    useEffect(function () {
        if (!enabled || completed || active || !user || !user.id || isParticipant) return;
        timerRef.current = setTimeout(function () {
            if (window.location.pathname !== '/' && window.location.pathname !== '/dashboard') {
                history.push('/');
            }
            setTimeout(function () { dispatch(Actions.startWalkthrough()); }, 300);
        }, 1000);
        return function () { clearTimeout(timerRef.current); };
    }, [enabled, completed, active, user, dispatch, history]);

    // Check status
    useEffect(function () {
        if (user && user.id) dispatch(Actions.checkWalkthroughStatus());
    }, [user, dispatch]);

    // Show a step: run onEnter, then poll for selector
    var showStep = useCallback(function (stepIndex) {
        if (timerRef.current) clearTimeout(timerRef.current);
        setReady(false);
        // Don't clear spotRect/tipPos - keep the old spotlight visible
        // so there's no flash. The CSS transition will animate to the new position.

        if (!steps.length || stepIndex >= steps.length) return;
        var step = steps[stepIndex];

        // Run onEnter action (navigate, expand menu, etc.)
        if (step.onEnter) {
            step.onEnter({ history: history, dispatch: dispatch });
        }

        // Poll for target
        var attempts = 0;
        var maxMs = step.skipAfter || 6000;
        var maxAttempts = Math.ceil(maxMs / 100);

        function poll() {
            // Check condition
            if (step.condition && !step.condition()) {
                // Skip this step
                dispatch(Actions.setWalkthroughStep(stepIndex + 1));
                return;
            }

            var waitEl = findTarget(step.waitFor || step.selector);
            if (waitEl && waitEl.getBoundingClientRect().height > 0) {
                // waitFor confirms readiness; spotlight always goes on selector
                var el = findTarget(step.selector) || waitEl;
                var r = el.getBoundingClientRect();
                var spot = getRect(el, step.padding);
                setSpotRect(spot);
                setCursorPos({ top: r.top + r.height / 2, left: r.left + r.width / 2 });

                // Position tooltip with estimated size, then re-measure after render
                var tw = tipSize.current.w, th = tipSize.current.h;
                setTipPos(tooltipPosition(spot, tw, th, step.side));
                setReady(true);

                // Re-measure after React renders the new content
                requestAnimationFrame(function () {
                    if (tooltipRef.current) {
                        var actualW = tooltipRef.current.offsetWidth;
                        var actualH = tooltipRef.current.offsetHeight;
                        if (actualW !== tw || actualH !== th) {
                            tipSize.current = { w: actualW, h: actualH };
                            setTipPos(tooltipPosition(spot, actualW, actualH, step.side));
                        }
                    }
                });
                return;
            }

            attempts++;
            if (attempts < maxAttempts) {
                timerRef.current = setTimeout(poll, 100);
            } else {
                // Skip step - target never appeared
                dispatch(Actions.setWalkthroughStep(stepIndex + 1));
            }
        }

        // Start polling after a brief delay for DOM to update
        timerRef.current = setTimeout(poll, step.onEnter ? 400 : 50);
    }, [steps, dispatch, history]);

    // When currentStep changes, show the step
    useEffect(function () {
        if (active) showStep(currentStep);
    }, [active, currentStep, showStep]);

    // Fade in on first activation - delay so browser renders opacity:0 first
    useEffect(function () {
        if (active && !fadeIn) {
            var t = setTimeout(function () { setFadeIn(true); }, 50);
            return function () { clearTimeout(t); };
        }
        if (!active) setFadeIn(false);
    }, [active, fadeIn]);

    // Reposition on resize/scroll
    useEffect(function () {
        if (!active || !ready) return;
        function reposition() {
            if (!steps.length || currentStep >= steps.length) return;
            var step = steps[currentStep];
            var el = findTarget(step.selector);
            if (el && el.getBoundingClientRect().height > 0) {
                var spot = getRect(el, step.padding);
                setSpotRect(spot);
                var r = el.getBoundingClientRect();
                setCursorPos({ top: r.top + r.height / 2, left: r.left + r.width / 2 });
                var tw = tipSize.current.w, th = tipSize.current.h;
                setTipPos(tooltipPosition(spot, tw, th, step.side));
            }
        }
        window.addEventListener('resize', reposition);
        window.addEventListener('scroll', reposition, true);
        return function () {
            window.removeEventListener('resize', reposition);
            window.removeEventListener('scroll', reposition, true);
        };
    }, [active, ready, steps, currentStep]);

    // Advance to next step
    var handleNext = useCallback(function () {
        setReady(false);
        var next = currentStep + 1;
        if (next >= steps.length) {
            dispatch(Actions.completeWalkthrough());
        } else {
            dispatch(Actions.setWalkthroughStep(next));
        }
    }, [currentStep, steps, dispatch]);

    var handleBack = useCallback(function () {
        if (currentStep > 0) {
            setReady(false);
            dispatch(Actions.setWalkthroughStep(currentStep - 1));
        }
    }, [currentStep, dispatch]);

    var handleSkip = useCallback(function () {
        dispatch(Actions.completeWalkthrough());
    }, [dispatch]);

    // Cleanup timer
    useEffect(function () {
        return function () { if (timerRef.current) clearTimeout(timerRef.current); };
    }, []);

    if (!active || !steps.length || currentStep >= steps.length) return null;

    var step = steps[currentStep];

    var arrowStyle = {};
    var showArrow = true;
    if (tipPos) {
        if (tipPos.arrowSide === 'top') arrowStyle = { top: -6, left: tipPos.arrowLeft };
        else if (tipPos.arrowSide === 'bottom') arrowStyle = { bottom: -6, left: tipPos.arrowLeft };
        else if (tipPos.arrowSide === 'left') arrowStyle = { left: -6, top: 20 };
        else if (tipPos.arrowSide === 'right') arrowStyle = { right: -6, top: 20 };
        else showArrow = false;
    }

    return (
        <React.Fragment>
            <div className={classes.overlay} />

            {spotRect && (
                <React.Fragment>
                    <div className={classes.spotlight} style={{
                        top: spotRect.top, left: spotRect.left,
                        width: spotRect.width, height: spotRect.height,
                        opacity: fadeIn ? 1 : 0
                    }} />
                    <div className={classes.pulseRing} style={{
                        top: spotRect.top - 4, left: spotRect.left - 4,
                        width: spotRect.width + 8, height: spotRect.height + 8
                    }} />
                </React.Fragment>
            )}

            {cursorPos && (
                <svg className={classes.cursor} style={{
                    top: cursorPos.top, left: cursorPos.left, width: 24, height: 24,
                    opacity: fadeIn ? 1 : 0, transition: 'top 0.5s ease, left 0.5s ease, opacity 0.4s ease'
                }} viewBox="0 0 24 24" fill="none">
                    <path d="M5 3l14 8-6.5 1.5L11 19z" fill="#fff" stroke="#3b82f6" strokeWidth="1.5"/>
                </svg>
            )}

            {tipPos && (
                <div ref={tooltipRef} className={classes.tooltip} style={{
                    top: tipPos.top, left: tipPos.left,
                    opacity: ready && fadeIn ? 1 : 0, transition: 'top 0.4s ease, left 0.4s ease, opacity 0.3s ease',
                    pointerEvents: ready ? 'auto' : 'none'
                }}>
                    {showArrow && <div className={classes.tooltipArrow} style={arrowStyle} />}
                    <div className={classes.stepCounter}>
                        Step {currentStep + 1} of {steps.length}
                    </div>
                    <div style={{ marginBottom: 4 }}>{step.content || step.text}</div>
                    <div className={classes.buttonRow}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Button className={classes.skipButton} onClick={handleSkip} size="small">
                                Skip tour
                            </Button>
                            {currentStep > 0 && (
                                <Button className={classes.skipButton} onClick={handleBack} size="small">
                                    <Icon style={{ fontSize: 14, marginRight: 2 }}>arrow_back</Icon>
                                    Back
                                </Button>
                            )}
                        </div>
                        <Button className={classes.nextButton} onClick={handleNext} size="small" variant="contained">
                            {currentStep + 1 >= steps.length ? 'Finish' : 'Next'}
                            <Icon style={{ fontSize: 16, marginLeft: 4 }}>
                                {currentStep + 1 >= steps.length ? 'check' : 'arrow_forward'}
                            </Icon>
                        </Button>
                    </div>
                </div>
            )}
        </React.Fragment>
    );
};

export default Walkthrough;
